import React, { useState, useContext, ReactElement } from 'react'

import i18n from 'live/services/i18n'
import styled from 'styled-components'

import { ErrorCard } from './ErrorCard'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'

interface DataErrorsSidebarProps {
    localErrors: LocalErrorsTypes[]
    hierarchyErrors: HierarchyErrorsTypes[]
    setIsErrorsSidebarOpen: (value: boolean) => void
    setExpandedRowKeysByEntity: (entity: keyof ExpandedRowKeys, keys: React.Key[]) => void
    handleExpandAll: () => void
}

export const DataErrorsSidebar = ({
    localErrors,
    hierarchyErrors,
    setIsErrorsSidebarOpen,
    setExpandedRowKeysByEntity,
    handleExpandAll
}: DataErrorsSidebarProps): ReactElement => {
    const { data } = useContext(StaticDataImporterContext)
    const [lastClickedError, setLastClickedError] = useState(null)
    const [activeError, setActiveError] = useState('')

    const isLocalErrors = localErrors.length > 0
    const numberOfErrors = (isLocalErrors ? localErrors : hierarchyErrors).length

    return (
        <DataErrorsSidebarContainer>
            <DataErrorsHeader>
                <button className="toggle-collapse-btn" onClick={() => setIsErrorsSidebarOpen(false)}>
                    <i className="fas fa-chevron-right" />
                </button>
                <h2 className="data-error-title"> {i18n('Error Validation')}</h2>
            </DataErrorsHeader>

            <DataErrorsMain>
                <h1>{i18n(`${isLocalErrors ? 'Local' : 'Hierarchy'} errors detected`)}</h1>
                <span className="explanation-span">
                    {i18n('There are some errors that you need to fix before proceeding with the transfer.')}
                </span>
                <span className="number-of-errors-span">{`${numberOfErrors} ${
                    numberOfErrors > 1 ? i18n('errorsKey') : i18n('errorKey')
                } ${i18n('found')}`}</span>
                {(isLocalErrors ? localErrors : hierarchyErrors).map(error => {
                    return (
                        <ErrorCard
                            key={error.uuid}
                            error={error}
                            lastClickedError={lastClickedError}
                            activeError={activeError}
                            setActiveError={setActiveError}
                            setLastClickedError={setLastClickedError}
                            setExpandedRowKeysByEntity={setExpandedRowKeysByEntity}
                            data={data}
                            handleExpandAll={handleExpandAll}
                        />
                    )
                })}
            </DataErrorsMain>
        </DataErrorsSidebarContainer>
    )
}

const DataErrorsSidebarContainer = styled.aside`
    display: flex;
    flex-direction: column;
    width: 400px;

    border-left: 2px solid var(--gray-400);
    background-color: var(--gray-100);

    overflow-y: auto;

    // Changing scrollbar css
    /* width */
    ::-webkit-scrollbar {
        width: 8px;
    }

    /* outside */
    ::-webkit-scrollbar-track {
        background: var(--gray-100);
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
        border-radius: 8px;
        background: var(--gray-500);
    }
`

const DataErrorsHeader = styled.header`
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 16px;

    border-bottom: 1px solid var(--gray-400);
    background-color: var(--gray-0);

    .toggle-collapse-btn {
        width: 30px;
        height: 30px;

        font-size: 14px;

        border-radius: 3px;
        border: 1px solid var(--gray-400);
        background-color: transparent;

        &:hover {
            opacity: 0.8;
        }
    }

    .data-error-title {
        margin: 0;

        font-size: 16px;
        font-weight: 700;
        line-height: 16px;

        color: var(--gray-800);
    }
`

const DataErrorsMain = styled.main`
    padding: 16px;
    display: flex;
    flex-direction: column;

    h1 {
        font-size: 20px;
        line-height: 24px;
        font-weight: 700;

        color: var(--gray-800);
    }

    .explanation-span {
        margin-top: 12px;
        margin-bottom: 16px;
        font-size: 13px;
        line-height: 16px;
        font-weight: 400;
    }

    .number-of-errors-span {
        margin-bottom: 8px;
        font-size: 13px;
        line-height: 18px;
    }
`
